import { useState } from 'react';
import { flushSync } from 'react-dom';
import { fitScale, A4_WIDTH_PX, A4_HEIGHT_PX } from '@core/doc/preview';
import { Printer, FileDown, Send } from 'lucide-react';
import { renderQuotationPreview } from '@core/doc/quotationRender';
import { printCapabilities } from '@data/services/printService';
import { printQuotation, saveQuotationPdf } from '@data/services/quotationPrintService';

import { DocumentScreen } from '@features/documents/DocumentScreen';
import { useQuotation } from './useQuotations';
import { useIssueDocument } from '@features/documents/useDocuments';
import { toast } from '@ui/components/Toaster';
import { IssueTransitionError } from '@data/services/issueTransitionService';
import { useConvertQuotationToSalesOrder } from '@features/salesOrders/useSalesOrders';

const assets = new URL('assets/', document.baseURI).href;
export function QuotationDocumentScreen({ documentId }: { documentId: string }) {
  const query = useQuotation(documentId);
  const capabilities = printCapabilities();
  const issueMutation = useIssueDocument();
  const convertMutation = useConvertQuotationToSalesOrder();
  const [busy, setBusy] = useState(false);
  const [pages, setPages] = useState(1);
  if (query.isLoading) return <p className="p-4 text-sm text-slate-400">Loading quotation…</p>;
  if (query.isError || !query.data) return <p role="alert" className="p-4 text-sm text-red-300">Could not open this quotation.</p>;
  const { model, stored, business } = query.data;
  if (stored.docType !== 'QUOTATION') return <DocumentScreen documentId={documentId}/>;
  async function print() {
    setBusy(true);
    try {
      await printQuotation(model, business, assets);
    } catch (e) { toast({ title: 'Could not print quotation', description: e instanceof Error ? e.message : 'Try again.', tone: 'danger' }); }
    finally { flushSync(() => setBusy(false)); }
  }
  async function convertToSalesOrder() {
    setBusy(true);
    try {
      const result = await convertMutation.mutateAsync(documentId);
      toast({ title: `Sales order ${result.docNumber} created as draft`, tone: 'success' });
      window.location.hash = `#/sales/orders/${result.id}/edit`;
    } catch (e) {
      toast({ title: 'Could not convert quotation', description: e instanceof Error ? e.message : 'Try again.', tone: 'danger' });
    } finally { setBusy(false); }
  }
  async function issue() {
    setBusy(true);
    try {
      await issueMutation.mutateAsync(documentId);
      await query.refetch();
      toast({ title: `Quotation ${stored.docNumber} issued`, tone: 'success' });
    } catch (e) { toast({ title: 'Could not issue quotation', description: e instanceof IssueTransitionError || e instanceof Error ? e.message : 'Try again.', tone: 'danger' }); }
    finally { setBusy(false); }
  }
  const scale = fitScale(window.innerWidth * .64);
  const html = renderQuotationPreview(model, business, assets, scale);
  return <div className="flex flex-col items-center gap-3 p-4 text-slate-100">
    <header className="flex w-full max-w-4xl flex-wrap items-center justify-between gap-3"><div><h1 className="font-semibold">Quotation {stored.docNumber}</h1><p data-testid="quotation-status" className="text-xs text-slate-400">{stored.status === 'DRAFT' ? 'Draft' : 'Issued'} · no stock or receivable</p></div><div className="flex gap-2">{stored.status === 'DRAFT' && <a className="rounded border border-slate-700 px-3 py-2 text-sm" href={`#/sales/quotations/${documentId}/edit`}>Edit draft</a>}{stored.status === 'DRAFT'&&<button data-testid="issue-quotation" disabled={busy} onClick={()=>void issue()} className="rounded bg-emerald-700 px-3 py-2 text-sm"><Send className="mr-1 inline h-4 w-4"/>Issue quotation</button>}{stored.status === 'ISSUED' && !query.data.salesOrderId && <button data-testid="convert-to-sales-order" disabled={busy} onClick={()=>void convertToSalesOrder()} className="rounded border border-slate-700 px-3 py-2 text-sm">Create sales order</button>}{query.data.salesOrderId && <a className="rounded border border-slate-700 px-3 py-2 text-sm" href={`#/sales/orders/${query.data.salesOrderId}`}>Open sales order</a>}<button disabled={busy} onClick={()=>void print()} className="rounded bg-brand-500 px-3 py-2 text-sm"><Printer className="mr-1 inline h-4 w-4"/>Print / Reprint</button>{capabilities.silentPdf&&<button disabled={busy} onClick={()=>void saveQuotationPdf(model, business, assets)} className="rounded border border-slate-700 px-3 py-2 text-sm"><FileDown className="mr-1 inline h-4 w-4"/>Save PDF</button>}</div></header>
    <iframe title="Quotation preview" data-testid="quotation-preview" srcDoc={html} style={{ width:A4_WIDTH_PX*scale,height:A4_HEIGHT_PX*scale,border:0 }} onLoad={()=>setPages(stored.coverLetterEnabled ? 2 : 1)} />
    <p className="text-xs text-slate-500">Preview · {pages} page{pages===1?'':'s'}</p>
  </div>;
}
